// src/components/FilterBar.tsx (v2)
// サイドバーのフィルタ群
import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { fiscalYearOf, fyDate } from "../lib/fiscalYear";
import { useFilters, resolvePeriod, monthShift, setMetaDateMax, urlHasAll, KB_ACTS, PROFIT_TABS, INPATIENT_COMPARE_TABS, DOCTOR_TOP_N_OPTIONS, GUN_DEFAULT, PROFIT_BASIS_DISCHARGE_ENABLED, type PeriodPreset, type PlFormat, type PlProfit, type BillingReconMode, type DoctorTopN, type RouteKeiyu } from "../store";
import { billingBunruiName } from "@shared/api";
import { api } from "../lib/api";
import { useDoctors } from "../lib/useDoctors";
import type { MeisaiMoversResponse } from "@shared/api";
import { useAuth } from "../lib/useAuth";
import { DiseaseSelector } from "./DiseaseSelector";
import { DpcBranchSelector } from "./DpcBranchSelector";
import { diseasePeriod } from "../lib/useDiseaseRanking";
import type { MetaResponse, Iryo, Yotei, Granularity, MetricRow } from "@shared/api";
import { KPI_TAB_METRIC_KEYS } from "./prototypes/budgetSpec";
import { gpText, IS_THETA } from "../lib/gpMode";
import { plMonthRange, clampPlMonth } from "../lib/plMonthRange";

const GRAN_OPTS: Granularity[] = ["大", "中", "小"];
const IRYO_OPTS: Iryo[]  = ["合計", "急性期", "回復期"];
// [急性期ベース 2026-06-26] DPC分析タブの医療区分トグルは廃止(急性期のみ)。
const YOTEI_OPTS: Yotei[] = ["合計", "予定",   "緊急"];
const ROUTE_KEIYU_OPTS: RouteKeiyu[] = ["合計", "病院", "総CL", "その他"];
const PERIOD_PRESETS: PeriodPreset[] = ["月次推移", "直近12ヶ月平均", "年度累計"];
const PL_FORMATS: PlFormat[] = ["月別", "年度累計", "直近12ヶ月累計"];
// [2026-09-11] 経営PL タブの利益の段階。 選択で「経費等」 の中身と利益の行が入れ替わる。
const PL_PROFITS: PlProfit[] = ["経常利益", "医業利益"];
// [2026-09-11 ユーザ裁定] 医業外損益は 経費等 に畳まず独立させたので、 経費等 はどちらでも同じ。
//   トグルが変えるのは「グラフをどちらの利益で締めるか」 だけ。
const PL_PROFIT_NOTE: Record<PlProfit, string> = {
  "経常利益": "医業利益 + 医業外損益。グラフに医業外損益の段が入る",
  "医業利益": "医業収益 − 医業費用（本業だけ）",
};

// 集計区分の表示名 (内部キーは変えず、 dropdown ラベルだけ書き換え)
const METRIC_LABEL: Record<string, string> = {
  "入院単価": "1日入院単価",
  "在院単価": "1日在院単価",
  "GP": "粗利",
  "GP単価": "1日粗利単価",
  // [2026-08-24] 生名「GP単価_在院」が正式版の指標一覧にそのまま出ていたので追加
  "GP単価_在院": "1日粗利単価(在院)",
  "DPC期間2以内率": "DPCⅡ以内率",
  "常勤換算数": "常勤医師数(入院診療科)",
  "入院単価(DPC期間2以内)": "1日入院単価(DPCⅡ以内)",
  "入院単価(DPC期間2超)": "1日入院単価(DPCⅡ超)",
};

// 4層比較(日次/速報/確報/予算)の年月セレクタ(2026-07-09 akira指示でサイドバーへ)。
//   選択肢= /api/query/billing_compare の months(=日次データが持つ月)。
//   queryKey はパネル側と同一("billing_compare")= react-query が dedup するので追加リクエストなし。
function BillingCompareMonthSection() {
  const f = useFilters();
  const { data } = useQuery({
    queryKey: ["billing_compare"], queryFn: api.billingCompare, staleTime: 60_000,
  });
  const months: string[] = data?.months ?? [];
  if (months.length === 0) return null;
  const cur = f.billingCompareMonth ?? months[months.length - 1];
  const jp = (iso: string) => {
    // サイドバー年月表記ルール = "YY年MM月" ゼロ埋め (MonthSelect / 各パネル pill と統一)。
    const [y, m] = iso.split("-");
    return `${y.slice(2)}年${m.padStart(2, "0")}月`;
  };
  return (
    <Section label="📅 4層比較 年月">
      <div className="flex flex-col gap-1">
        {months.slice().reverse().map((m) => (
          <label key={m} className="flex items-center gap-1.5 text-[12px] cursor-pointer">
            <input type="radio" name="billingCompareMonth" checked={m === cur}
              onChange={() => f.setBillingCompareMonth(m)} />
            {jp(m)}
          </label>
        ))}
      </div>
      <div className="text-[10px] text-slate-400 mt-1 leading-tight">
        下段「4層比較（日次/速報/確報/予算）」の表示月。
      </div>
    </Section>
  );
}

export function FilterBar({ meta, onCollapse }: { meta: MetaResponse; onCollapse?: () => void }) {
  const f = useFilters();
  const { canViewDoctor } = useAuth();
  const isSurgery = f.tab === "手術";
  const isDisease = f.tab === "DPC分析";
  const isPl = f.tab === "経営PL";
  // [2026-08-03] 入院実績(前年度・予算比較) と 入院実績(医師別) は同じ期間コントロールを使う
  const isInpatientBudgetCompare = INPATIENT_COMPARE_TABS.includes(f.tab);
  const isInpatientDoctor = f.tab === "入院実績(医師別)";
  // [2026-08-22] 入院KPI 廃止により 入院実績(前年度・予算比較)/(医師別) だけが対象
  const isKpiLike = isInpatientBudgetCompare;
  const isKb = f.tab === "行為別";   // 行為別タブは 対象月+表示形式 のみ (科/行為はパネル内で選択)
  const isKbAct = f.tab === "行為別売上";  // [2026-07-18] 行為別売上: 表示指標+医療区分/予定緊急/粒度
  const isRoute = f.tab === "入院経路";   // [2026-08-25] サイドバーに「経由」フォーカスを出す
  // [2026-09-07 ユーザ指示] 行為別売上も 入院実績 と同じ「年度内の 開始月/終了月」で期間を指定し月平均にする。
  //   対象月/表示形式 (直近12ヶ月平均) は出さず、periodFrom/To の既定・維持も 入院実績 と同じ規則にする。
  // 入院経路も「診療科×月」の年度比較表で、入院実績と同じ開始月/終了月を使う。
  const usesFyPeriod = isInpatientBudgetCompare || isKbAct || isRoute;
  const isOpeRp = f.tab === "手術売上";     // [2026-07-18] 手術売上: 期間+診療科フォーカスのみ
  const isMeisai = f.tab === "明細分析";  // 明細分析は 対象月 のみ (区分/分類/基準はパネル内)
  const isCorr = f.tab === "相関分析";  // 相関分析は 開始/終了月 + 粒度 + 絞り込み + y軸 のみ
  const isProfit = PROFIT_TABS.includes(f.tab);  // 粗利分析(医師別あり/なし)は 開始/終了月 + 粒度 + 予定緊急 + 延数 + 群分類閾値
  // 速報進捗(請求会計速報)は モード切替(入院/入外合計)+ ドリル選択chip のみ(他タブの共通UIは隠す)。
  //   ○日は endpoint が自動採用するため 対象月/表示形式/医療区分/予定緊急/粒度/フォーカス/注目指標 は不要。
  const isBilling = f.tab === "請求会計速報";
  const opeDateTo = f.opeTo ?? f.asOfMonth;
  const opeDateFrom = f.opeFrom ?? f.asOfMonth;
  const opeYotei = f.yotei === "合計" ? null : f.yotei;
  // 手術売上パネルと同じ queryKey を使い、術式候補・一致件数を追加通信なしで共有する。
  const { data: opeRpData, isFetching: isOpeRpFetching } = useQuery({
    queryKey: [
      "surgery_rp",
      opeDateFrom,
      opeDateTo,
      f.focusedDept,
      f.granularity,
      opeYotei,
      f.opeProcedureQuery,
    ],
    queryFn: () => api.surgeryRp({
      dateFrom: opeDateFrom,
      dateTo: opeDateTo,
      dept: f.focusedDept,
      granularity: f.granularity,
      yotei: opeYotei,
      procedureQuery: f.opeProcedureQuery || null,
    }),
    enabled: isOpeRp,
    placeholderData: (p) => p,
  });

  // 起動時に asOfMonth を dateMax に合わせる (= 最新確定月)。
  // ただし URL に "?as=..." が明示されていれば尊重 (PL タブで過去年度を見るとき等)。
  //   App(正式版) は自前で解決するが、BetaApp(/beta) はここが唯一の解決点なので両対応で残す。
  //   setMetaDateMax: /beta でも URL sync の as= 既定判定 (最新月なら出さない) を効かせる。
  useEffect(() => {
    if (meta.dateMax) setMetaDateMax(meta.dateMax);
    // [URL 明示の尊重] ?as= があれば既定 (最新月) で上書きしない。判定は store の共通ヘルパーへ集約。
    const urlAs = urlHasAll("as");
    if (meta.dateMax && f.asOfMonth !== meta.dateMax && !urlAs) {
      f.setAsOfMonth(meta.dateMax);
      // preset 適用
      // [2026-09-07] ?from&?to が明示された deep link は preset で潰さない (下の preset effect と同じガード)。
      //   これが無いと 入院実績/行為別売上 の共有URL (from/to のみ・as なし) が開いた瞬間に「最新月〜最新月」へ
      //   戻っていた (ヘッドレス描画で実測: from=2026-04-01&to=2026-07-01 → 7月〜7月)。
      if (!urlHasAll("from", "to")) {
        const { periodFrom, periodTo } = resolvePeriod(
          f.periodPreset, meta.dateMax, meta.dateMin, meta.dateMax
        );
        f.setPeriodFrom(periodFrom);
        f.setPeriodTo(periodTo);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [meta.dateMin, meta.dateMax]);

  // preset が変わったら periodFrom/To を再計算
  useEffect(() => {
    if (usesFyPeriod) return;
    // [事故対策 2026-07-22] ?from&?to が明示されていれば preset 由来の既定で上書きしない。
    //   共有URL・静的生成 (bin/generate.mjs) は期間を URL で固定して開くため、
    //   ここで潰すと「指定した期間と違うページ」が出来上がる (入院実績タブで実際に事故った)。
    if (urlHasAll("from", "to")) return;
    const { periodFrom, periodTo } = resolvePeriod(
      f.periodPreset, f.asOfMonth, meta.dateMin, meta.dateMax
    );
    if (periodFrom !== f.periodFrom) f.setPeriodFrom(periodFrom);
    if (periodTo !== f.periodTo) f.setPeriodTo(periodTo);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [f.periodPreset, f.asOfMonth, meta.dateMin, meta.dateMax, usesFyPeriod]);

  // [2026-09-26 ユーザ指示] 経営PL の対象月は PL の実績がある月だけ (範囲と寄せ方 = lib/plMonthRange.ts)。
  //   PlPanel と同じ queryKey なので追加通信は無い。
  const { data: plData } = useQuery({
    queryKey: ["pl"],
    queryFn: api.pl,
    staleTime: 5 * 60_000,
    enabled: isPl,
  });
  const plRange = isPl ? plMonthRange(plData, f.plFormat) : null;

  // 入院実績(前年度・予算比較): 期間は年度内の月番号だけで指定する。
  // [初期表示 2026-07-22 ユーザ指示] 既定を「4月〜最新月」→「最新月〜最新月」(= 単月) に変更。
  //   タブを開いた直後は当月の実績を見たいケースが大半のため。年度累計で見たいときは
  //   サイドバーの ↺ ボタン (4月〜最新月) か 開始月セレクタで戻せる。
  //   deps は [タブ, dateMax] なのでタブに入った時だけ発火 = タブ内で月を変えても上書きしない。
  //   [2026-09-07] 対象タブは 入院実績(前年度・予算比較)/(医師別) + 行為別売上 (usesFyPeriod)。この 3 タブの間を
  //   直接行き来しても usesFyPeriod は true のままなので発火せず、選んだ期間はそのまま引き継がれる
  //   (ユーザ要望「行為別売上も入院実績と同じように」= 同じ期間で両タブを見比べる用途を優先。意図した挙動)。
  useEffect(() => {
    if (!usesFyPeriod) return;
    const latest = meta.dateMax;
    if (!latest) return;
    // 共有URL・静的PDF生成は from/to を明示して期間を固定する。明示値まで最新単月へ
    // 上書きすると「年度月平均」が実際には単月になるため、両方ある場合は store 初期値を尊重する。
    if (urlHasAll("from", "to")) return;
    const [ly, lm] = latest.split("-").map(Number);
    const def = `${ly}-${String(lm).padStart(2, "0")}-01`;
    if (f.periodFrom !== def) f.setPeriodFrom(def);
    if (f.periodTo !== def) f.setPeriodTo(def);
    if (f.asOfMonth !== latest) f.setAsOfMonth(latest);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [usesFyPeriod, meta.dateMax]);

  // 粒度に応じて metric ドロップダウン表示を絞る。
  // 入院実績タブでは「このタブで使う指標」だけに絞る (2026-06-15)。
  const visibleMetrics: MetricRow[] = meta.metrics.filter((m) => {
    if (isKpiLike && !KPI_TAB_METRIC_KEYS.has(m["集計区分"])) return false;
    if (m.granularities === "all") return true;
    if (m.granularities === "小only") return f.granularity === "小";
    return true;
  });

  // 粒度=中/大 では metric が利用不可なら入院収入にフォールバック
  useEffect(() => {
    if (!visibleMetrics.find((m) => m["集計区分"] === f.metric)) {
      f.setMetric("入院収入");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [f.granularity]);

  // 明細分析タブ: フィルタ用の 診療No分類 / 診療科 一覧を取得 (パネルと同じ queryKey でキャッシュ共有)
  const meisaiWindow = f.periodPreset === "直近12ヶ月平均" ? 12 : 1;
  const { data: meisaiData } = useQuery<MeisaiMoversResponse>({
    queryKey: ["meisai_movers", f.asOfMonth, f.meisaiIo, f.meisaiBasis, f.meisaiSinryoNo, f.meisaiDept, meisaiWindow],
    queryFn: () => api.meisaiMovers({ asOfMonth: f.asOfMonth, io: f.meisaiIo, basis: f.meisaiBasis, sinryoNo: f.meisaiSinryoNo || undefined, dept: f.meisaiDept || undefined, window: meisaiWindow, topN: 30 }),
    enabled: isMeisai,
    placeholderData: (p) => p,
  });

  return (
    <aside className="card p-4 sticky top-12 self-start max-h-[calc(100vh-3.5rem)] overflow-y-auto space-y-4 text-sm">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-1">
          {onCollapse && (
            <button
              type="button"
              onClick={onCollapse}
              title="サイドバーを折りたたむ"
              className="text-slate-400 hover:text-slate-700 px-1 text-base leading-none"
            >◀</button>
          )}
          <h2 className="text-base font-semibold">フィルタ</h2>
        </div>
        {/* 2026-05-28 ユーザ指示: すべてデフォルトに戻すボタン (= store 全 filter リセット) */}
        <button
          type="button"
          onClick={() => useFilters.setState({
            granularity:         "小",
            iryo:                "合計",
            yotei:               "合計",
            focusedDept:         null,
            surgeryFocusedDept:  null,
            dpc6:                null,
            admdrcd:             null,
            dpcfy:               2026,
            dateFromOverride:    null,
            dateToOverride:      null,
            diseaseYotei:        "合計",
            diseaseIryo:         "合計",
            dpcAgg:              "累計",
            revenueBasis:        "入院収入",   // [共通化 2026-07-22] 収入の基準 (全タブ共通)
            selectedDpc7:        null,
            selectedDpc8:        null,
            selectedDpc10:       null,
            selectedDpc11:       null,
            selectedDpc12:       null,
            selectedDpc13:       null,
            selectedDpc14:       null,
            minCases:            0,
            profitBasis:         "在院",
            profitFrom:          null,
            profitTo:            null,
            profitIryo:          "急性期",
            gun1:                GUN_DEFAULT[0],
            gun2:                GUN_DEFAULT[1],
            gun3:                GUN_DEFAULT[2],
            kpiVolumeBase:       "入院",
            routeKeiyu:          "合計",
          })}
          className="text-[10px] px-2 py-0.5 rounded border border-slate-300 hover:bg-slate-100 text-slate-700"
          title="すべての filter を デフォルト値に戻す (= 改定年度=2026 / 粒度=小 / フォーカス・医師・疾患 解除)"
        >
          ⟲ デフォルトに戻す
        </button>
      </div>

      {/* 速報進捗(請求会計速報)タブ: モード切替(入院/入外合計)+ ドリル選択chip + クリア。
          ○日 は endpoint が自動採用するため 対象月/表示形式/医療区分/予定緊急/粒度/フォーカス/注目指標 は出さない。 */}
      {isBilling && (
        <>
          <Section label="📋 表示モード">
            {/* 区分は 入院 / 入外合計 の 2 つのみ(2026-07-04)。外来モードは未検証のため UI から隠す
                (store / 型 / endpoint data は温存し将来復活可能)。 */}
            <RadioGroup
              name="billingReconMode" value={f.billingReconMode}
              onChange={(v) => f.setBillingReconMode(v as BillingReconMode)}
              options={(["入院", "入外合計"] as BillingReconMode[]).map((o) => ({ value: o, label: o }))}
            />
            <div className="text-[10px] text-slate-400 mt-1 leading-tight">
              入院=表示科→診療No→科×診療No の3表ドリル。入外合計=入院/外来の1表。
            </div>
          </Section>
          <BillingCompareMonthSection />
          {f.billingReconMode === "入院" && (
            <Section label="🎯 入院ドリル 絞り込み">
              {!f.billingReconDept && !f.billingReconBunrui ? (
                <div className="text-[10px] text-slate-400 leading-tight">
                  ①表示科 / ②診療No の行をクリックすると絞り込めます。
                </div>
              ) : (
                <div className="space-y-1">
                  {f.billingReconDept && (
                    <button type="button" onClick={() => f.setBillingReconDept(null)}
                      className="flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full bg-blue-100 text-blue-800 hover:bg-blue-200"
                      title="表示科の絞り込みを解除">
                      科: {f.billingReconDept} <span className="font-bold">×</span>
                    </button>
                  )}
                  {f.billingReconBunrui && (
                    <button type="button" onClick={() => f.setBillingReconBunrui(null)}
                      className="flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full bg-violet-100 text-violet-800 hover:bg-violet-200"
                      title="診療Noの絞り込みを解除">
                      診療No: {f.billingReconBunrui}:{billingBunruiName(f.billingReconBunrui)} <span className="font-bold">×</span>
                    </button>
                  )}
                  <button type="button" onClick={() => f.clearBillingReconFilter()}
                    className="block text-[10px] text-red-600 hover:underline mt-1">× すべての絞り込みを解除</button>
                </div>
              )}
            </Section>
          )}
        </>
      )}

      {/* 粗利分析タブ: [2026-07-17 ユーザ指示で再構成]
          期間基準 → 開始月 → 終了月 → 医療区分 → 予定緊急 → 診療科粒度 → 診療科フォーカス
          → DPC絞り込み(退院日のみ) → 群分類 → 単価切替。注目指標は出さない。
          絞り込みはすべてこのサイドバーに凝縮する (パネル上部にチップを散らさない)。 */}
      {isProfit && (() => {
        const minYM = meta.dateMin.slice(0, 7), maxYM = meta.dateMax.slice(0, 7);
        // [2026-07-17] 粗利専用の期間 state。null = 最新確定月のみ (default)
        const latest = `${maxYM}-01`;
        const fYM = (f.profitFrom ?? latest).slice(0, 7), tYM = (f.profitTo ?? latest).slice(0, 7);
        const periodOverridden = f.profitFrom != null || f.profitTo != null;
        const isDischarge = f.profitBasis === "退院";
        const gunRows: [string, number, (v: number) => void][] = [["1群≥", f.gun1, f.setGun1], ["2群≥", f.gun2, f.setGun2], ["3群≥", f.gun3, f.setGun3]];
        return (
          <>
            {/* ★2026-08-27 ユーザ指示: 退院日基準を封鎖中 (PROFIT_BASIS_DISCHARGE_ENABLED=false)。
                理由と復活手順は store.ts の同定数の docstring。封鎖中は選択肢が 在院日 だけに
                なるのでトグルごと出さない (押せないボタンを残さない)。 */}
            {PROFIT_BASIS_DISCHARGE_ENABLED && (
              <Section label="🕒 期間基準" help="在院日=在院月で集計(科別のみ・全タブと整合)。退院日=退院月で集計(DPCエピソード・科別/医師別/DPC別)">
                <div className="flex gap-1">
                  {(["在院", "退院"] as const).map((v) => (
                    <button key={v} type="button" onClick={() => f.setProfitBasis(v)}
                      className={`flex-1 px-2 py-1 rounded text-xs ${f.profitBasis === v ? (v === "退院" ? "bg-violet-600 text-white" : "bg-teal-600 text-white") : "bg-slate-100 hover:bg-slate-200"}`}>{v}日</button>
                  ))}
                </div>
                <div className="text-[10px] text-slate-400 mt-1">
                  {isDischarge ? "退院月で集計" : "在院月で集計"}
                </div>
              </Section>
            )}
            {/* [2026-07-18] 開始月/終了月に ←→ 月送り (DPC分析と同デザイン)。from<=to を境界で保証 */}
            {(() => {
              const navBtn = "px-2 py-1 border border-slate-300 rounded bg-white text-slate-600 hover:bg-slate-100 disabled:opacity-40 disabled:cursor-not-allowed text-xs";
              const setFrom = (ym: string) => { f.setProfitFrom(`${ym}-01`); if (f.profitTo == null) f.setProfitTo(latest); };
              const setTo   = (ym: string) => { f.setProfitTo(`${ym}-01`); if (f.profitFrom == null) f.setProfitFrom(latest); };
              const prevF = monthShift(`${fYM}-01`, -1).slice(0, 7), nextF = monthShift(`${fYM}-01`, +1).slice(0, 7);
              const prevT = monthShift(`${tYM}-01`, -1).slice(0, 7), nextT = monthShift(`${tYM}-01`, +1).slice(0, 7);
              return (
                <>
                  <Section label="📅 開始月">
                    <div className="flex items-center gap-1">
                      <button type="button" className={navBtn} disabled={prevF < minYM}
                        onClick={() => prevF >= minYM && setFrom(prevF)} title="前月">←</button>
                      <MonthSelect value={fYM} min={minYM} max={tYM} onChange={setFrom} />
                      <button type="button" className={navBtn} disabled={nextF > tYM}
                        onClick={() => nextF <= tYM && setFrom(nextF)} title="翌月">→</button>
                    </div>
                  </Section>
                  <Section label="📅 終了月">
                    <div className="flex items-center gap-1">
                      <button type="button" className={navBtn} disabled={prevT < fYM}
                        onClick={() => prevT >= fYM && setTo(prevT)} title="前月">←</button>
                      <MonthSelect value={tYM} min={fYM} max={maxYM} onChange={setTo} />
                      <button type="button" className={navBtn} disabled={nextT > maxYM}
                        onClick={() => nextT <= maxYM && setTo(nextT)} title="翌月">→</button>
                    </div>
                    {periodOverridden && (
                      <button type="button" onClick={() => { f.setProfitFrom(null); f.setProfitTo(null); }}
                        className="text-[10px] text-blue-600 hover:underline mt-1">↺ 最新月のみに戻す</button>
                    )}
                  </Section>
                </>
              );
            })()}
            <Section label="医療区分">
              {/* 在院日基準のみ有効 (退院日基準は DPC エピソード = 急性期のみ)。
                  [2026-07-18] 粗利専用 state profitIryo (default=急性期・共通 iryo から独立)。 */}
              <RadioGroup
                name="profitIryo" value={isDischarge ? "急性期" : f.profitIryo}
                onChange={(v) => f.setProfitIryo(v as "合計" | "急性期" | "回復期")}
                options={IRYO_OPTS.map((o) => ({ value: o, label: o, disabled: isDischarge && o !== "急性期" }))}
              />
              {isDischarge && (
                <div className="text-[10px] text-slate-400 mt-1 leading-tight">退院日基準は急性期のみ</div>
              )}
            </Section>
            <Section label="予定緊急">
              <RadioGroup
                name="yotei" value={f.yotei}
                onChange={(v) => f.setYotei(v as Yotei)}
                options={YOTEI_OPTS.map((o) => ({ value: o, label: o }))}
              />
            </Section>
            <Section label="診療科粒度">
              <RadioGroup
                name="granularity" value={f.granularity}
                onChange={(v) => f.setGranularity(v as Granularity)}
                options={GRAN_OPTS.map((o) => ({ value: o, label: `${o}分類` }))}
              />
            </Section>
            <Section label="🎯 診療科フォーカス">
              <select
                className="w-full border border-slate-300 rounded px-2 py-1 bg-white"
                value={f.focusedDept ?? ""}
                onChange={(e) => f.setFocusedDept(e.target.value === "" ? null : e.target.value)}
              >
                <option value="">— 病院全体 —</option>
                {meta.deptsAll
                  .filter((d) => d.粒度 === f.granularity && !d.is_total)
                  .map((d) => (
                    <option key={d["診療科"]} value={d["診療科"]}>{d["診療科"]}</option>
                  ))}
              </select>
              {f.focusedDept && (
                <button
                  onClick={() => f.setFocusedDept(null)}
                  className="mt-1 text-[10px] text-red-600 hover:underline"
                >× フォーカスを解除</button>
              )}
            </Section>
            <Section label="🏷 群分類 閾値" help={gpText("1日粗利単価で 1〜4 群に分類。4群 = 3群閾値未満")}>
              <div className="space-y-1 text-xs">
                {gunRows.map(([lbl, val, setter], i) => (
                  <label key={i} className="flex items-center gap-1">
                    <span className="w-10 text-slate-600">{lbl}</span>
                    {/* [2026-07-18] スピナーは 5,000 円刻み */}
                    <input type="number" step={5000} value={val} onChange={(e) => setter(Number(e.target.value) || 0)}
                      className="flex-1 min-w-0 border border-slate-300 rounded px-1.5 py-0.5 bg-white tabular-nums" />
                    <span className="text-slate-400">円</span>
                  </label>
                ))}
              </div>
            </Section>
            <Section label="💴 単価切替" help="1日単価と患者延数の分母 (入院延数 / 在院延数) を切替">
              <RadioGroup
                name="kpiVolumeBase" value={f.kpiVolumeBase}
                onChange={(v) => f.setKpiVolumeBase(v as "入院" | "在院")}
                options={[{ value: "入院", label: "入院単価" }, { value: "在院", label: "在院単価" }]}
              />
            </Section>
            {/* [絞り込み保持 2026-07-17→07-18] 退院日基準のみ DPC別表があるので、DPC分析と同じ
                疾患セレクタ + 分岐セレクタ 3 本を出す (科別からの深掘り用・タブ間で共有)。
                [2026-07-18 ユーザ指示] サイドバーの一番下に配置。 */}
            {isDischarge && (
              <>
                <Section label="🔬 DPC 疾患名/コード">
                  <DiseaseSelector />
                </Section>
                <Section label="🔪 DPC 手術分類" help="上で疾患を選ぶと候補がその配下に絞られます。DPC別表の行クリックでも入ります">
                  <DpcBranchSelector level={10} />
                </Section>
                <Section label="🧬 DPC 処置1分類">
                  <DpcBranchSelector level={11} />
                </Section>
                <Section label="🧬 DPC 処置2分類">
                  <DpcBranchSelector level={12} />
                </Section>
                <DpcClearAllButton />
              </>
            )}
          </>
        );
      })()}

      {/* 相関分析タブ: 開始月 / 終了月 (←→で月送り) (各月=1サンプル、 既定=直近1年) */}
      {isCorr && (() => {
        const minYM = meta.dateMin.slice(0, 7), maxYM = meta.dateMax.slice(0, 7);
        const effTo = (f.corrTo || meta.dateMax);
        const effFrom = (f.corrFrom || monthShift(effTo, -11));   // "" = 直近1年(12ヶ月)
        const fYM = effFrom.slice(0, 7), tYM = effTo.slice(0, 7);
        const navBtn = "px-2 py-1 border border-slate-300 rounded bg-white text-slate-600 hover:bg-slate-100 disabled:opacity-40 disabled:cursor-not-allowed";
        return (
          <>
            <Section label="📅 開始月">
              <div className="flex items-center gap-1">
                <button type="button" className={navBtn} disabled={fYM <= minYM}
                  onClick={() => f.setCorrFrom(monthShift(effFrom, -1))} title="前月">←</button>
                <MonthSelect value={fYM} min={minYM} max={tYM}
                  onChange={(ym) => f.setCorrFrom(`${ym}-01`)} />
                <button type="button" className={navBtn} disabled={monthShift(effFrom, 1).slice(0, 7) > tYM}
                  onClick={() => f.setCorrFrom(monthShift(effFrom, 1))} title="翌月">→</button>
              </div>
            </Section>
            <Section label="📅 終了月">
              <div className="flex items-center gap-1">
                <button type="button" className={navBtn} disabled={monthShift(effTo, -1).slice(0, 7) < fYM}
                  onClick={() => f.setCorrTo(monthShift(effTo, -1))} title="前月">←</button>
                <MonthSelect value={tYM} min={fYM} max={maxYM}
                  onChange={(ym) => f.setCorrTo(`${ym}-01`)} />
                <button type="button" className={navBtn} disabled={tYM >= maxYM}
                  onClick={() => f.setCorrTo(monthShift(effTo, 1))} title="翌月">→</button>
              </div>
              <div className="text-[10px] text-slate-400 mt-1 leading-tight">
                各月=1サンプル ({((parseInt(tYM.slice(0,4))*12+parseInt(tYM.slice(5))) - (parseInt(fYM.slice(0,4))*12+parseInt(fYM.slice(5))) + 1)}ヶ月)。
                <button type="button" onClick={() => { f.setCorrFrom(""); f.setCorrTo(""); }}
                  className="text-slate-500 hover:underline ml-1">↺ 直近1年</button>
              </div>
            </Section>
            <div className="text-[10px] text-slate-400 leading-tight px-1">
              指標の選択はグラフ下のパネルで行います。
            </div>
          </>
        );
      })()}

      {usesFyPeriod && (
        <InpatientBudgetComparePeriodSelect meta={meta} />
      )}

      {!isDisease && !isCorr && !isProfit && !isBilling && !usesFyPeriod && !isOpeRp && (
        <Section label="対象月">
          {/* 入力欄 + ←/→ 矢印で月送り (dateMin/Max で disable) */}
          {(() => {
            const asOf = clampPlMonth(f.asOfMonth, plRange);
            const asYM = asOf.slice(0, 7);
            const minYM = isPl && plRange ? plRange.min : meta.dateMin.slice(0, 7);
            const maxYM = isPl && plRange ? plRange.max : meta.dateMax.slice(0, 7);
            const prevDate = monthShift(asOf, -1);
            const nextDate = monthShift(asOf, +1);
            const canPrev = prevDate.slice(0, 7) >= minYM;
            const canNext = nextDate.slice(0, 7) <= maxYM;
            const navBtn = "px-2 py-1 border border-slate-300 rounded bg-white text-slate-600 hover:bg-slate-100 disabled:opacity-40 disabled:cursor-not-allowed";
            return (
              <div className="flex items-center gap-1">
                <button type="button" className={navBtn} disabled={!canPrev}
                        onClick={() => canPrev && f.setAsOfMonth(prevDate)}
                        title="前月">←</button>
                <MonthSelect
                  value={asYM}
                  min={minYM}
                  max={maxYM}
                  onChange={(ym) => f.setAsOfMonth(`${ym}-01`)}
                />
                <button type="button" className={navBtn} disabled={!canNext}
                        onClick={() => canNext && f.setAsOfMonth(nextDate)}
                        title="翌月">→</button>
              </div>
            );
          })()}
        </Section>
      )}

      {/* 経営PL タブ: 表示形式 = 月別 / 年度累計 / 直近12ヶ月累計 (PL 全セクション一元制御) */}
      {isPl && (
        <Section label="表示形式">
          <select
            className="w-full border border-slate-300 rounded px-2 py-1 bg-white"
            value={f.plFormat}
            onChange={(e) => f.setPlFormat(e.target.value as PlFormat)}
          >
            {PL_FORMATS.map((p) => <option key={p} value={p}>{p}</option>)}
          </select>
        </Section>
      )}

      {/* 経営PL タブ: 利益の段階 = 医業利益 / 経常利益。
          サマリーカードは 12 枚固定で両方の利益を出すので、 ここが効くのは
          損益トレンドの積み上げと ウォーターフォールの締め (医業外損益 の段が入るかどうか)。
          ★ラベルは「利益指標」 ではなく「利益の段階」 (段階利益 = 会計の呼び方。
            「利益指標」 だと利益系 KPI の選択と読まれる) — 2026-09-11 敵対レビュー。 */}
      {isPl && (
        <Section label="利益の段階">
          <select
            className="w-full border border-slate-300 rounded px-2 py-1 bg-white"
            value={f.plProfit}
            onChange={(e) => f.setPlProfit(e.target.value as PlProfit)}
          >
            {PL_PROFITS.map((p) => <option key={p} value={p}>{p}</option>)}
          </select>
          <div className="text-xs text-slate-400 mt-1">{PL_PROFIT_NOTE[f.plProfit]}</div>
        </Section>
      )}

      {!isDisease && !isPl && !isCorr && !isProfit && !isBilling && !usesFyPeriod && !isOpeRp && (
        <Section label="表示形式">
          <select
            className="w-full border border-slate-300 rounded px-2 py-1 bg-white"
            value={f.periodPreset}
            onChange={(e) => f.setPeriodPreset(e.target.value as PeriodPreset)}
          >
            {/* 年度累計 は要件未定のため当面 選択不可 (2026-06-15)。 コントロールは残す */}
            {PERIOD_PRESETS.map((p) => (
              <option key={p} value={p} disabled={p === "年度累計"}>
                {p}{p === "年度累計" ? "(準備中)" : ""}
              </option>
            ))}
          </select>
          {/* 年月表記は明細分析のみ表示 (入院KPI/行為別/手術/入院経路/元データ では非表示、ユーザ要望 2026-06-18) */}
          {isMeisai && (
            <div className="text-xs text-slate-400 mt-1">
              {meisaiWindow === 12 ? "直近12ヶ月 vs 前年同期" : "選択月 vs 前年同月"}
            </div>
          )}
        </Section>
      )}

      {/* 明細分析タブ: 区分 / 基準 / 診療No分類 / 診療科 */}
      {isMeisai && (
        <>
          <Section label="区分">
            <RadioGroup name="meisaiIo" value={f.meisaiIo}
              onChange={(v) => f.setMeisaiIo(v as "入院" | "外来" | "全")}
              options={[{ value: "入院", label: "入院" }, { value: "外来", label: "外来" }, { value: "全", label: "全" }]} />
          </Section>
          <Section label="基準">
            <RadioGroup name="meisaiBasis" value={f.meisaiBasis === "yen" ? "金額" : "点数"}
              onChange={(v) => f.setMeisaiBasis(v === "金額" ? "yen" : "ten")}
              options={[{ value: "点数", label: "点数" }, { value: "金額", label: "金額" }]} />
          </Section>
          <Section label="診療No分類">
            <select className="w-full border border-slate-300 rounded px-2 py-1 bg-white"
                    value={f.meisaiSinryoNo} onChange={(e) => f.setMeisaiSinryoNo(e.target.value)}>
              <option value="">— すべて —</option>
              {(meisaiData?.sinryoNos ?? []).map((s) => <option key={s.name} value={s.name}>{s.no}:{s.name}</option>)}
            </select>
          </Section>
          <Section label="診療科">
            <select className="w-full border border-slate-300 rounded px-2 py-1 bg-white"
                    value={f.meisaiDept} onChange={(e) => f.setMeisaiDept(e.target.value)}>
              <option value="">— 全科 —</option>
              {(meisaiData?.depts ?? []).map((d) => <option key={d} value={d}>{d.replace(/^HP\)/, "")}</option>)}
            </select>
          </Section>
        </>
      )}

      {isDisease && (
        <Section label="📊 改定年度">
          {/* [2026-07-17] 2026 年度 (令和8) を追加し default 化。
              期間 (from/to) の自動セットは setDpcfy が override を null に戻し、
              DiseasePanel 側の effect が disease_period_range で解決する (一元化)。 */}
          <select
            className="w-full border border-slate-300 rounded px-2 py-1 bg-white"
            value={f.dpcfy ?? 2026}
            onChange={(e) => f.setDpcfy(Number(e.target.value))}
          >
            <option value="2026">2026 年度</option>
            <option value="2024">2024 年度</option>
          </select>
        </Section>
      )}

      {isDisease && (
        <Section
          label="📅 期間"
          right={<DiseasePeriodRightLabel />}
        >
          <DiseasePeriodSelect meta={meta} />
        </Section>
      )}

      {/* [2026-09-28 ユーザ指示] 件数・金額の出し方をタブ内で統一 (主表/サマリ/ICD別/実績K)。既定=期間累計・左 */}
      {isDisease && (
        <Section label="🧮 件数・金額の表示"
                 help="退院数・件数・入院収入(粗利)を 期間の合計 で出すか、データのある月数で割った 月平均 で出すか。DPC分析タブの表すべてに効きます。入院日数中央値・DPCⅡ以内率・1日単価はどちらでも同じ値です">
          <RadioGroup
            name="dpcAgg" value={f.dpcAgg}
            onChange={(v) => f.setDpcAgg(v as "累計" | "月平均")}
            options={[{ value: "累計", label: "期間累計" }, { value: "月平均", label: "月平均" }]}
          />
        </Section>
      )}

      {/* [急性期ベース 2026-06-26] 医療区分トグル廃止 — DPC分析タブは急性期のみ。
          診療科は退院時診療科での絞り込みにのみ使う(科別の実績テーブルは無し)。 */}

      {/* [桁切替 2026-07-01] 集計レベル(6~14桁)はサイドバーから表直上の「タブ」(DpcTopBar)へ移動。 */}

      {isDisease && (
        <Section label="🚑 予定緊急">
          <RadioGroup
            name="diseaseYotei"
            value={f.diseaseYotei}
            onChange={(v) => f.setDiseaseYotei(v as "合計" | "予定" | "緊急")}
            options={YOTEI_OPTS.map((o) => ({ value: o, label: o }))}
          />
        </Section>
      )}

      {/* [2026-07-17] 粗利分析は isProfit ブロック内でユーザ指定順に出すため共通側からは除外 */}
      {!isSurgery && !isDisease && !isKb && !isMeisai && !isCorr && !isBilling && !isProfit && !isOpeRp && (
        <Section label="医療区分">
          <RadioGroup
            name="iryo" value={f.iryo} onChange={(v) => f.setIryo(v as Iryo)}
            options={IRYO_OPTS.map((o) => ({ value: o, label: o }))}
          />
        </Section>
      )}

      {!isDisease && !isKb && !isMeisai && !isCorr && !isBilling && !isProfit && !isOpeRp && (
        <Section label="予定緊急">
          <RadioGroup
            name="yotei" value={f.yotei}
            onChange={(v) => f.setYotei(v as Yotei)}
            options={YOTEI_OPTS.map((o) => ({ value: o, label: o }))}
          />
        </Section>
      )}

      {!isSurgery && !isKb && !isMeisai && !isBilling && !isProfit && !isOpeRp && (
        <Section label="診療科粒度">
          <RadioGroup
            name="granularity" value={f.granularity}
            onChange={(v) => f.setGranularity(v as Granularity)}
            options={GRAN_OPTS.map((o) => ({ value: o, label: `${o}分類` }))}
          />
        </Section>
      )}

      {f.tab === "入院実績" && canViewDoctor && (
        <Section label="医師" help="その在院日の主治医で絞り込みます。医師を選ぶと診療科の選択を解除します。">
          <DoctorSelect />
        </Section>
      )}

      {/* 手術タブ: 集計粒度トグル (大分類=診療科 ⇄ 小分類=手術科)。 サイドバーに配置 (ユーザ要望 2026-06-17) */}
      {isSurgery && (
        <Section label="集計粒度">
          <RadioGroup
            name="surgeryDim" value={f.surgeryDim}
            onChange={(v) => f.setSurgeryDim(v as "大分類" | "小分類")}
            options={[{ value: "大分類", label: "診療科" }, { value: "小分類", label: "手術科小分類" }]}
          />
          <div className="text-[10px] text-slate-400 mt-1 leading-tight">
            小分類は 外科を術者所属で 消化器 / 乳腺内分泌 / 呼吸器外科 に分割 (2024年4月以降)。
          </div>
        </Section>
      )}

      {/* [2026-07-18] 行為別売上でも診療科フォーカス有効 (表行クリックと連動・グラフがその科に切替)。
          手術売上 (isOpeRp) は専用セクションで出すためここでは除外 */}
      {!isMeisai && !isBilling && !isProfit && !isOpeRp && (isSurgery ? (
        <Section label={`診療科フォーカス (手術台帳・${f.surgeryDim})`}>
          <select
            className="w-full border border-slate-300 rounded px-2 py-1 bg-white"
            value={f.surgeryFocusedDept ?? ""}
            onChange={(e) => f.setSurgeryFocusedDept(e.target.value === "" ? null : e.target.value)}
          >
            <option value="">— 病院全体 —</option>
            {(f.surgeryDim === "小分類" ? meta.surgeryDeptsSmall : meta.surgeryDepts).map((d) => (
              <option key={d} value={d}>{d}</option>
            ))}
          </select>
          {f.surgeryFocusedDept && (
            <button
              onClick={() => f.setSurgeryFocusedDept(null)}
              className="mt-1 text-[10px] text-red-600 hover:underline"
            >× フォーカスを解除</button>
          )}
          <div className="text-[10px] text-slate-400 mt-1 leading-tight">
            手術台帳 (ope_sera) 由来。 集計粒度トグル ({f.surgeryDim}) に追従。 入院 (収入分析) と科名が異なる場合あり
            (例: 脳神経外科 / 脳血管内治療科 が分離。 小分類では 外科→消化器/乳腺内分泌/呼吸器)。
          </div>
        </Section>
      ) : (
        <Section label="🎯 診療科フォーカス">
          <select
            className="w-full border border-slate-300 rounded px-2 py-1 bg-white"
            value={f.focusedDept ?? ""}
            onChange={(e) => f.setFocusedDept(e.target.value === "" ? null : e.target.value)}
          >
            <option value="">— 病院全体 —</option>
            {meta.deptsAll
              .filter((d) => d.粒度 === f.granularity && !d.is_total)
              .map((d) => (
                <option key={d["診療科"]} value={d["診療科"]}>
                  {d["診療科"]}
                </option>
              ))}
          </select>
          {f.focusedDept && (
            <button
              onClick={() => f.setFocusedDept(null)}
              className="mt-1 text-[10px] text-red-600 hover:underline"
            >× フォーカスを解除</button>
          )}
        </Section>
      ))}

      {/* [2026-08-25 ユーザ指示] 入院経路タブに加え、入院実績・GP分析でも 経由 (病院/総CL/その他) で
          サイドバーから絞り込める。入院実績・GP分析(在院基準)は fact_long_keiyu が小分類しか
          持たないため 粒度=小 のときだけ有効 (中/大はdisable表示)。GP分析(退院基準)は
          fact_disease_episode が全粒度で経由グループを持つので粒度を問わず有効。 */}
      {(isRoute || isInpatientBudgetCompare || isProfit) && (() => {
        const profitDischarge = isProfit && f.profitBasis === "退院";
        const grainOk = isRoute || profitDischarge || f.granularity === "小";
        return (
          <Section label="🚪 経由" help="新規入院の経由先。総CL=総合クリニック、その他=腎CL+地域ケア。経由データが無い(未取得)入院は集計から除外される — 直近1〜2ヶ月や2023年より前の古い期間は取得率が低いので、その期間を含む集計では合計が「経由=合計」より小さくなることがある">
            {!grainOk ? (
              <div className="text-[11px] text-slate-400 leading-tight">
                小分類でのみ利用可 (現在: {f.granularity}分類)
              </div>
            ) : (
              <>
                <RadioGroup
                  name="routeKeiyu" value={f.routeKeiyu}
                  onChange={(v) => f.setRouteKeiyu(v as RouteKeiyu)}
                  options={ROUTE_KEIYU_OPTS.map((o) => ({ value: o, label: o }))}
                />
                {f.routeKeiyu !== "合計" && (
                  <button
                    onClick={() => f.setRouteKeiyu("合計")}
                    className="mt-1 text-[10px] text-red-600 hover:underline"
                  >× 絞り込みを解除</button>
                )}
                {isProfit && f.routeKeiyu !== "合計" && !profitDischarge && (
                  <div className="text-[10px] text-amber-700 mt-1 leading-tight">
                    🔶 医師人件費はGP構成比で按分した推定値(実測ではない)
                  </div>
                )}
              </>
            )}
          </Section>
        );
      })()}

      {/* 行為別タブ: 注目指標 = 行為 (集計区分)。 科別マトリックスの対象 + 行為別マトリックスで強調 */}
      {isKb && (
        <Section label="📌 注目指標 (行為)">
          <select className="w-full border border-slate-300 rounded px-2 py-1 bg-white"
                  value={f.kbAct} onChange={(e) => f.setKbAct(e.target.value)}>
            {KB_ACTS.map((a) => <option key={a} value={a}>{a}</option>)}
          </select>
          <div className="text-[10px] text-slate-400 mt-1 leading-tight">科別マトリックスの対象 + 行為別マトリックスで強調</div>
        </Section>
      )}

      {/* [共通化 2026-07-22] 行為別売上の「金額基準」はサイドバー最下部の共通「収入の基準」に統合。 */}

      {/* [2026-07-18] 行為別売上: 表示指標 (グラフ+科×月表の値を一括切替) */}
      {isKbAct && (
        <Section label="📌 表示指標" help="グラフと科別月別表の値を切替。手術(輸血除く) = 手術/手術材料/手術薬剤 (輸血・麻酔はその他へ)">
          <select
            className="w-full border border-slate-300 rounded px-2 py-1 bg-white"
            value={f.kbActMetric}
            onChange={(e) => f.setKbActMetric(e.target.value as typeof f.kbActMetric)}
          >
            {/* [2026-08-22] 収入の基準が控除後のときは総額の呼び名を「薬剤材料費差引後入院収入」に
                (カード・見出し・CSV と同じ。fact_long_all の同名指標と同値) */}
            {(["入院収入", "入院料", "手術", "その他"] as const).map((m) => (
              <option key={m} value={m}>
                {m === "手術" ? "手術(輸血除く)"
                 : m === "入院収入" ? (f.revenueBasis === "粗利" ? "薬剤材料費差引後入院収入 (総額)" : "入院収入 (総額)")
                 : m}
              </option>
            ))}
          </select>
        </Section>
      )}

      {/* [2026-07-21改] 手術売上: 集計期間(←→・既定=最新月の1ヶ月) → 予定緊急 → 粒度 → フォーカス */}
      {isOpeRp && (() => {
        const fromD = f.opeFrom ?? f.asOfMonth;
        const toD = f.opeTo ?? f.asOfMonth;
        const fYM = fromD.slice(0, 7), tYM = toD.slice(0, 7);
        const minYM = meta.dateMin.slice(0, 7), maxYM = meta.dateMax.slice(0, 7);
        const navBtn = "px-2 py-1 border border-slate-300 rounded bg-white text-slate-600 hover:bg-slate-100 disabled:opacity-40 disabled:cursor-not-allowed text-xs";
        const setFrom = (ym: string) => f.setOpeFrom(`${ym}-01`);
        const setTo   = (ym: string) => f.setOpeTo(`${ym}-01`);
        const prevF = monthShift(`${fYM}-01`, -1).slice(0, 7), nextF = monthShift(`${fYM}-01`, +1).slice(0, 7);
        const prevT = monthShift(`${tYM}-01`, -1).slice(0, 7), nextT = monthShift(`${tYM}-01`, +1).slice(0, 7);
        const overridden = f.opeFrom != null || f.opeTo != null;
        return (
          <>
            <Section label="表示する売上">
              <div className="grid grid-cols-2 gap-1">
                {(["手術売上", "手技売上", "材料薬剤費"] as const).map((mode) => (
                  <button
                    key={mode}
                    type="button"
                    aria-pressed={f.opeRevenueMode === mode}
                    className={`px-1 py-1 text-xs rounded border ${
                      mode === "手術売上" ? "col-span-2" : ""
                    } ${
                      f.opeRevenueMode === mode
                        ? "bg-emerald-600 border-emerald-600 text-white font-semibold"
                        : "bg-white border-slate-300 text-slate-600 hover:bg-slate-50"
                    }`}
                    onClick={() => f.setOpeRevenueMode(
                      f.opeRevenueMode === mode && mode !== "手術売上" ? "手術売上" : mode,
                    )}
                  >
                    {mode}
                  </button>
                ))}
              </div>
            </Section>
            <Section label="📅 開始月">
              <div className="flex items-center gap-1">
                <button type="button" className={navBtn} disabled={prevF < minYM}
                  onClick={() => prevF >= minYM && setFrom(prevF)} title="前月">←</button>
                <MonthSelect value={fYM} min={minYM} max={tYM} onChange={setFrom} />
                <button type="button" className={navBtn} disabled={nextF > tYM}
                  onClick={() => nextF <= tYM && setFrom(nextF)} title="翌月">→</button>
              </div>
            </Section>
            <Section label="📅 終了月">
              <div className="flex items-center gap-1">
                <button type="button" className={navBtn} disabled={prevT < fYM}
                  onClick={() => prevT >= fYM && setTo(prevT)} title="前月">←</button>
                <MonthSelect value={tYM} min={fYM} max={maxYM} onChange={setTo} />
                <button type="button" className={navBtn} disabled={nextT > maxYM}
                  onClick={() => nextT <= maxYM && setTo(nextT)} title="翌月">→</button>
              </div>
              {overridden && (
                <button type="button" onClick={() => { f.setOpeFrom(null); f.setOpeTo(null); }}
                  className="text-[10px] text-blue-600 hover:underline mt-1">↺ 最新月に戻す</button>
              )}
            </Section>
            <Section
              label="🔎 術式"
              help="Kコードまたは術式名の一部を入力。診療科別・手術別グラフと一覧を同じ条件で絞り込みます"
              right={f.opeProcedureQuery
                ? `${opeRpData?.rows.length ?? 0} Kコード`
                : `${opeRpData?.procedures.length ?? 0} Kコードから検索`}
            >
              <div className="relative">
                <input
                  type="search"
                  list="ope-procedure-options"
                  className="w-full border border-slate-300 rounded px-2 py-1 pr-7 bg-white text-xs"
                  value={f.opeProcedureQuery}
                  maxLength={200}
                  placeholder="Kコード・術式名を入力"
                  aria-label="術式をKコードまたは術式名で絞り込み"
                  onChange={(e) => f.setOpeProcedureQuery(e.target.value)}
                />
                {f.opeProcedureQuery && (
                  <button
                    type="button"
                    className="absolute right-1.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700"
                    aria-label="術式の絞り込みを解除"
                    onClick={() => f.setOpeProcedureQuery("")}
                  >
                    ×
                  </button>
                )}
                <datalist id="ope-procedure-options">
                  {(opeRpData?.procedures ?? []).map((r) => (
                    <option
                      key={r.kcode}
                      value={`${r.kcode} ${r.kname}`}
                      label={r.tencd_count > 1 ? `${r.tencd_count}診療行為を集約` : undefined}
                    />
                  ))}
                </datalist>
              </div>
              {isOpeRpFetching && (
                <div className="text-[10px] text-slate-400 mt-1">検索中…</div>
              )}
              {f.opeProcedureQuery && !isOpeRpFetching && (opeRpData?.rows.length ?? 0) === 0 && (
                <div className="text-[10px] text-amber-700 mt-1">一致する術式がありません</div>
              )}
            </Section>
            <Section label="予定緊急">
              <div className="flex rounded overflow-hidden border border-slate-300">
                {YOTEI_OPTS.map((y) => (
                  <button key={y}
                    className={`flex-1 px-1 py-1 text-xs ${f.yotei === y ? "bg-emerald-600 text-white font-semibold" : "bg-white text-slate-600 hover:bg-slate-50"}`}
                    onClick={() => f.setYotei(y)}
                  >{y}</button>
                ))}
              </div>
            </Section>
            <Section label="診療科粒度">
              <div className="flex rounded overflow-hidden border border-slate-300">
                {GRAN_OPTS.map((g) => (
                  <button key={g}
                    className={`flex-1 px-1 py-1 text-xs ${f.granularity === g ? "bg-emerald-600 text-white font-semibold" : "bg-white text-slate-600 hover:bg-slate-50"}`}
                    onClick={() => { f.setGranularity(g); f.setFocusedDept(null); }}
                    title="入院主科の粒度 (行為別売上と同軸)。切替でフォーカスは解除"
                  >{g}分類</button>
                ))}
              </div>
            </Section>
            <Section label="🎯 診療科フォーカス" help="入院主科 (手術日の在院科・行為別売上と同じ振り分け) で絞り込み">
              <select
                className="w-full border border-slate-300 rounded px-2 py-1 bg-white"
                value={f.focusedDept ?? ""}
                onChange={(e) => f.setFocusedDept(e.target.value === "" ? null : e.target.value)}
              >
                <option value="">— 全科 —</option>
                {meta.deptsAll
                  .filter((d) => d.粒度 === f.granularity && !d.is_total)
                  .map((d) => <option key={d["診療科"]} value={d["診療科"]}>{d["診療科"]}</option>)}
              </select>
            </Section>
          </>
        );
      })()}

      {!isSurgery && !isDisease && !isKb && !isMeisai && !isCorr && !isBilling && !isInpatientBudgetCompare && !isProfit && !isKbAct && !isOpeRp && !isRoute && (
        <Section label="📌 注目指標">
          <select
            className="w-full border border-slate-300 rounded px-2 py-1 bg-white"
            value={f.metric}
            onChange={(e) => f.setMetric(e.target.value)}
          >
            {visibleMetrics.map((m) => (
              <option key={m["集計区分"]} value={m["集計区分"]}>
                {gpText(METRIC_LABEL[m["集計区分"]] ?? m["集計区分"])} ({m.unit})
              </option>
            ))}
          </select>
        </Section>
      )}

      {isDisease && (
        <>
          {/* [2026-07-17 ユーザ指示] 主治医絞り込みは削除 (admdrcd state/URL 互換は温存)。 */}
          <Section label="🔬 DPC 疾患名/コード">
            <DiseaseSelector />
          </Section>

          {/* [2026-07-18 ユーザ指示] 手術分類/処置1分類/処置2分類 を独立セレクタに
              (DPC疾患名/コードと同作法・行クリックで飛んできた値も同期表示・粗利分析と共通)。 */}
          <Section label="🔪 DPC 手術分類" help="上で疾患を選ぶと候補がその配下に絞られます。主表の行クリックでも入ります">
            <DpcBranchSelector level={10} />
          </Section>
          <Section label="🧬 DPC 処置1分類">
            <DpcBranchSelector level={11} />
          </Section>
          <Section label="🧬 DPC 処置2分類">
            <DpcBranchSelector level={12} />
          </Section>
          <DpcClearAllButton />

          {/* [共通化 2026-07-22] DPC分析の「指標切替」は最下部の共通「収入の基準」に統合
              (入院実績/入院KPI/行為別売上 と同じトグル・タブを移っても選択を引き継ぐ)。 */}
          {/* [2026-07-17 ユーザ指示] 「退院数 N 件以上」絞り込みは削除 (minCases=0 固定運用)。 */}
        </>
      )}

      {/* 収入の基準 (入院収入 / 粗利)。サイドバー最下部 (2026-06-15)。
          [共通化 2026-07-22 ユーザ指示] 入院実績・入院KPI に加えて、行為別売上の「金額基準」と
          DPC分析の「指標切替」もこの 1 つに統合。タブを移っても選択を引き継ぐ (store: revenueBasis)。 */}
      {/* [開発版(新GP) 2026-08-24] /theta では「粗利」を「GP」と呼び、医師人件費まで引く。
          ★行為別売上タブだけは行為区分に人件費を帰属できないので、どちらのページでも
            「薬剤材料費差引後」(= 材料・薬剤のみ控除) のまま。 */}
      {(isKpiLike || isKbAct || isDisease) && (
        <Section label="収入の基準"
                 help={isKbAct
                   ? "薬剤材料費差引後 = 入院収入 − 出来高換算薬剤費 − 出来高換算材料費。控除の源は入院実績・DPC分析と同じ (EF の包括前)"
                     + (IS_THETA ? "。★このタブは医師人件費を控除していないので GP ではありません" : "")
                   : IS_THETA
                     ? "GP = 薬剤材料費差引後入院収入 − 医師人件費（= 入院収入 − 出来高換算薬剤費 − 出来高換算材料費 − 医師人件費）。医師人件費が揃う 2025年4月以降のみ値が入ります (タブを移っても引き継ぎます)"
                     : "粗利 = 収入から薬剤・材料の出来高換算額を差し引いた額。入院実績・行為別売上・DPC分析で共通 (タブを移っても引き継ぎます)"}>
          <RadioGroup
            name="revenueBasis" value={f.revenueBasis}
            onChange={(v) => f.setRevenueBasis(v as "入院収入" | "粗利")}
            options={[{ value: "入院収入", label: "入院収入" },
                      { value: "粗利", label: isKbAct ? "薬剤材料費差引後" : gpText("粗利") }]}
          />
          {f.revenueBasis === "粗利" && (
            <div className="text-[10px] text-emerald-700 mt-1 leading-tight">
              {isKbAct ? "材料・薬剤を除いた額で表示中 (医師人件費は未控除)"
                       : IS_THETA ? "材料・薬剤と医師人件費を除いた額で表示中"
                                  : "材料・薬剤を除いた額で表示中"}
            </div>
          )}
        </Section>
      )}
      {isKpiLike && (
        <Section label="延数の基準">
          <RadioGroup
            name="kpiVolumeBase" value={f.kpiVolumeBase}
            onChange={(v) => f.setKpiVolumeBase(v as "入院" | "在院")}
            options={[{ value: "入院", label: "入院延数" }, { value: "在院", label: "在院延数" }]}
          />
        </Section>
      )}
      {/* [2026-08-03] 入院実績(医師別): 医師別マトリックスの行数。
          印刷 1 枚に収まる範囲で選べるように 20 / 25 / 30 の 3 択。
          診療科フォーカス中はその科の医師を全員出すので、この設定は効かない。 */}
      {isInpatientDoctor && (
        <Section label="医師別の表示行数"
                 help="医師別マトリックスに出す行数 (入院収入の多い順)。印刷 1 枚に収めるための上限で、診療科で絞り込んだときも効きます (通常は 1 科 10 名前後なので全員入ります)">
          <RadioGroup
            name="doctorTopN" value={String(f.doctorTopN)}
            onChange={(v) => f.setDoctorTopN(Number(v) as DoctorTopN)}
            options={DOCTOR_TOP_N_OPTIONS.map((n) => ({ value: String(n), label: `上位${n}` }))}
          />
          {f.focusedDept && (
            <div className="text-[10px] text-amber-700 mt-1 leading-tight">
              🎯 {f.focusedDept} で絞り込み中 — この科の医師だけを表示しています
            </div>
          )}
        </Section>
      )}
    </aside>
  );
}

export function DoctorSelect() {
  const f = useFilters();
  const [search, setSearch] = useState("");
  const { canViewDoctor } = useAuth();
  const { data, isLoading, error } = useDoctors();
  // role ガード (defense-in-depth): 上位の Section も同じ判定で出さないが、
  // 万一 DoctorSelect を 直接呼ばれても 何も表示しない。
  // ⚠ 全 hook を 呼んだ後に判定 (rules-of-hooks)
  if (!canViewDoctor) return null;
  // 2026-05-28 ユーザ指示: 「— 184例」 → 「— 月15例」 (= 月平均の新規受持件数)。
  // useDoctors と同じ期間 (= diseasePeriod + override) で月数を出して n_cases を割る。
  const { dateFrom: defFrom, dateTo: defTo } = diseasePeriod(f.asOfMonth, f.dpcfy, f.diseaseAutoPeriod);
  const dateFrom = f.tab === "入院実績" ? f.periodFrom : f.dateFromOverride ?? defFrom;
  const dateTo   = f.tab === "入院実績" ? f.asOfMonth : f.dateToOverride ?? defTo;
  const months   = Math.max(
    1,
    Math.round(
      (new Date(dateTo).getTime() - new Date(dateFrom).getTime()) / (1000 * 60 * 60 * 24 * 30.4),
    ),
  );
  const normalize = (value: string) => value.normalize("NFKC").replace(/\s/g, "").toLocaleLowerCase();
  const needle = f.tab === "入院実績" ? normalize(search) : "";
  const doctors = data?.rows ?? [];
  const matches = doctors.filter(d => normalize(`${d.医師名 ?? ""} ${d.admdrcd}`).includes(needle));
  const selected = doctors.find(d => d.admdrcd === f.admdrcd);
  const selectedOutsideSearch = selected && !matches.some(d => d.admdrcd === selected.admdrcd);
  if (error) {
    return (
      <div className="text-[10px] text-red-600">
        医師リスト取得失敗: {(error as Error).message}
      </div>
    );
  }
  return (
    <>
      {f.tab === "入院実績" && <>
        <input type="search" aria-label="医師名を検索" placeholder="医師名を検索"
          className="w-full border border-slate-300 rounded px-2 py-1 mb-1 bg-white"
          value={search} onChange={e => setSearch(e.target.value)} disabled={isLoading} />
        {search && <div className="text-[10px] text-slate-500 mb-1" role="status">
          {matches.length ? `候補 ${matches.length}名` : "一致する医師がいません"}
        </div>}
      </>}
      <select
        className="w-full border border-slate-300 rounded px-2 py-1 bg-white"
        aria-label="医師"
        value={f.admdrcd ?? ""}
        onChange={(e) => {
          const v = e.target.value === "" ? null : e.target.value;
          f.setAdmdrcd(v);
          // 医師を選んだら 診療科 filter を自動クリア (交差 0 件防止)。
          // 例: 診療科=循環器内科 で見ている時に 医師=坂本昇 (泌尿器科) を選ぶと
          // 「循環器内科 ∩ 坂本昇」 で常に 0 件になってしまう。
          // ⚠ 2026-05-28 bug fix: f.setFocusedDept(null) を呼ぶと
          //   store 側の副作用で admdrcd も null にリセットされる (store.ts setFocusedDept 参照)。
          //   そのため setState({ focusedDept: null }) を直接呼んで admdrcd は維持する。
          if (v !== null) {
            useFilters.setState({ focusedDept: null });
          }
        }}
        disabled={isLoading}
      >
        <option value="">— 全医師 —</option>
        {selectedOutsideSearch && <option value={selected.admdrcd}>{selected.医師名 || selected.admdrcd}（選択中）</option>}
        {matches.map((d) => (
          <option key={d.admdrcd} value={d.admdrcd}>
            {d["医師名"] || d.admdrcd}{f.tab !== "入院実績" && ` — 月${Math.round(d.n_cases / months)}例`}
          </option>
        ))}
      </select>
      {f.admdrcd && (
        <button
          type="button"
          onClick={() => f.setAdmdrcd(null)}
          className="mt-1 text-[10px] text-red-600 hover:underline"
        >× 医師フィルタ解除</button>
      )}
      {isLoading && (
        <div className="text-[10px] text-slate-400 mt-1">医師リスト読込中…</div>
      )}
    </>
  );
}

// [2026-09-07] 年度ヘルパは lib/fiscalYear.ts の共有版を使う (以前はここに同じロジックの重複実装があった)。

export function InpatientBudgetComparePeriodSelect({ meta }: { meta: MetaResponse }) {
  const f = useFilters();
  const fyStart = fiscalYearOf(meta.dateMax);
  const months = [4, 5, 6, 7, 8, 9, 10, 11, 12, 1, 2, 3];
  const fromMonth = Number(f.periodFrom.slice(5, 7));
  const toMonth = Number(f.periodTo.slice(5, 7));
  const label = (m: number) => `${m}月`;

  const setFromMonth = (month: number) => {
    const next = fyDate(fyStart, month);
    f.setPeriodFrom(next);
    if (months.indexOf(month) > months.indexOf(toMonth)) f.setPeriodTo(next);
  };
  const setToMonth = (month: number) => {
    const next = fyDate(fyStart, month);
    f.setPeriodTo(next);
    if (months.indexOf(month) < months.indexOf(fromMonth)) f.setPeriodFrom(next);
  };

  return (
    <>
      <Section label="📅 開始月">
        <select
          className="w-full border border-slate-300 rounded px-2 py-1 bg-white"
          aria-label="開始月"
          value={fromMonth}
          onChange={(e) => setFromMonth(Number(e.target.value))}
        >
          {months.map((m) => <option key={m} value={m}>{label(m)}</option>)}
        </select>
      </Section>
      <Section label="📅 終了月">
        <select
          className="w-full border border-slate-300 rounded px-2 py-1 bg-white"
          aria-label="終了月"
          value={toMonth}
          onChange={(e) => setToMonth(Number(e.target.value))}
        >
          {months.map((m) => <option key={m} value={m}>{label(m)}</option>)}
        </select>
        <button
          type="button"
          onClick={() => {
            f.setPeriodFrom(`${fyStart}-04-01`);
            f.setPeriodTo(meta.dateMax.slice(0, 8) + "01");
          }}
          className="text-[10px] text-slate-500 hover:underline mt-1"
        >↺ 4月〜最新月</button>
      </Section>
    </>
  );
}

// 疾患深掘りタブ専用: 期間 (from / to) 月選択 + 直近12ヶ月リセット
export function DiseasePeriodSelect({ meta }: { meta: MetaResponse }) {
  const f = useFilters();
  const { dateFrom: defFrom, dateTo: defTo } = diseasePeriod(f.asOfMonth, f.dpcfy, f.diseaseAutoPeriod);
  const fromYM = (f.dateFromOverride ?? defFrom).slice(0, 7);
  const toYM   = (f.dateToOverride   ?? defTo).slice(0, 7);
  // [2026-09-28 ユーザ指摘] 改定年度を選んでいるときは、その年度の範囲 (dpcfy-06 〜 dpcfy+2-05) の外へ
  //   開始/終了を動かせないようにする。従来はデータ全体 (meta) の範囲で選べたが、集計側
  //   (disease_ranking の dpcfyToRange) は年度外を落とすので、選んだ期間と実際に集計した期間がずれていた。
  const fyMin = f.dpcfy != null ? `${f.dpcfy}-06` : null;
  const fyMax = f.dpcfy != null ? `${f.dpcfy + 2}-05` : null;
  const metaMin = meta.dateMin.slice(0, 7), metaMax = meta.dateMax.slice(0, 7);
  const minYM  = fyMin && fyMin > metaMin ? fyMin : metaMin;
  const maxYM  = fyMax && fyMax < metaMax ? fyMax : metaMax;
  const overridden = f.dateFromOverride != null || f.dateToOverride != null;

  // input type="month" → "YYYY-MM-01" (from) / 月末 (to) に正規化
  const setFromYM = (ym: string) => {
    if (!ym) return;
    f.setDateFromOverride(`${ym}-01`);
    // to が未指定なら自動補完 (= 現在の to をそのまま固定)
    if (f.dateToOverride == null) f.setDateToOverride(defTo);
  };
  const setToYM = (ym: string) => {
    if (!ym) return;
    const [y, m] = ym.split("-").map(Number);
    const lastDay = new Date(Date.UTC(y, m, 0)).getUTCDate();
    f.setDateToOverride(`${ym}-${String(lastDay).padStart(2, "0")}`);
    if (f.dateFromOverride == null) f.setDateFromOverride(defFrom);
  };
  const resetToLast12 = () => {
    f.setDateFromOverride(null);
    f.setDateToOverride(null);
  };

  // ←/→ 矢印で月送り (入院実績タブの「対象月」 と同じパターン)
  // monthShift は "YYYY-MM-01" 形式を期待するので 「${ym}-01」 で渡す
  const navBtn = "px-2 py-1 border border-slate-300 rounded bg-white text-slate-600 hover:bg-slate-100 disabled:opacity-40 disabled:cursor-not-allowed text-xs";

  const prevFromYM = monthShift(`${fromYM}-01`, -1).slice(0, 7);
  const nextFromYM = monthShift(`${fromYM}-01`, +1).slice(0, 7);
  const canPrevFrom = prevFromYM >= minYM;
  // 開始は 終了月以下 でないと invalid (= from <= to を保証)
  const canNextFrom = nextFromYM <= toYM && nextFromYM <= maxYM;

  const prevToYM = monthShift(`${toYM}-01`, -1).slice(0, 7);
  const nextToYM = monthShift(`${toYM}-01`, +1).slice(0, 7);
  // 終了は 開始月以上 でないと invalid
  const canPrevTo = prevToYM >= fromYM && prevToYM >= minYM;
  const canNextTo = nextToYM <= maxYM;

  // [期間表示 2026-06-27→2026-07-17] "YYYY-MM" → "26年05月"。
  //   native <input type="month"> は OS/ブラウザが「2026年05月」固定表示で再整形不可
  //   = サイドバー幅で見切れるため、select (MonthSelect) に置換して "26年05月" 表記に統一。
  const periodLabel = fromYM === toYM ? ymShortLabel(fromYM) : `${ymShortLabel(fromYM)} 〜 ${ymShortLabel(toYM)}`;
  return (
    <div className="space-y-1">
      <div className="text-xs font-semibold text-brand">📅 {periodLabel}</div>
      <div className="flex flex-col gap-1">
        <label className="flex items-center gap-1">
          <span className="text-[10px] text-slate-500 w-8 shrink-0">開始</span>
          <button type="button" className={navBtn} disabled={!canPrevFrom}
                  onClick={() => canPrevFrom && setFromYM(prevFromYM)}
                  title="前月">←</button>
          <MonthSelect value={fromYM} min={minYM} max={toYM} onChange={setFromYM} />
          <button type="button" className={navBtn} disabled={!canNextFrom}
                  onClick={() => canNextFrom && setFromYM(nextFromYM)}
                  title="翌月">→</button>
        </label>
        <label className="flex items-center gap-1">
          <span className="text-[10px] text-slate-500 w-8 shrink-0">終了</span>
          <button type="button" className={navBtn} disabled={!canPrevTo}
                  onClick={() => canPrevTo && setToYM(prevToYM)}
                  title="前月">←</button>
          <MonthSelect value={toYM} min={fromYM} max={maxYM} onChange={setToYM} />
          <button type="button" className={navBtn} disabled={!canNextTo}
                  onClick={() => canNextTo && setToYM(nextToYM)}
                  title="翌月">→</button>
        </label>
      </div>
      {/* 「直近12ヶ月 (default)」 表示は Section の右側 (= DiseasePeriodRightLabel) に移動。
          ここには override 中のリセット button だけ残す。 */}
      {overridden && (
        <button
          type="button"
          onClick={resetToLast12}
          className="text-[10px] text-blue-600 hover:underline"
        >
          ↺ 直近12ヶ月にリセット
        </button>
      )}
    </div>
  );
}

// [期間表示 2026-07-17] "YYYY-MM" → "26年05月" (ゼロ埋め維持・年 2 桁)。
// native <input type="month"> は「2026年05月」固定表示でサイドバー幅に収まらないため、
// select ベースの MonthSelect に置換してこの表記に統一する (DPC分析 / 粗利分析)。
function ymShortLabel(ym: string): string {
  const [y, m] = ym.split("-");
  return `${y.slice(2)}年${m}月`;
}

function monthOptions(min: string, max: string): string[] {
  const out: string[] = [];
  let [y, m] = min.split("-").map(Number);
  const [my, mm] = max.split("-").map(Number);
  while ((y < my || (y === my && m <= mm)) && out.length < 400) {
    out.push(`${y}-${String(m).padStart(2, "0")}`);
    m += 1;
    if (m > 12) { m = 1; y += 1; }
  }
  return out;
}

export function MonthSelect({ value, min, max, onChange, className }: {
  value: string;                    // "YYYY-MM"
  min: string;                      // "YYYY-MM"
  max: string;                      // "YYYY-MM"
  onChange: (ym: string) => void;
  className?: string;
}) {
  const opts = monthOptions(min, max);
  // range 外の value (改定年度 range clamp 等) も表示できるよう option に含める
  if (value && !opts.includes(value)) {
    if (value < min) opts.unshift(value);
    else opts.push(value);
  }
  return (
    <select
      className={className ?? "flex-1 min-w-0 border border-slate-300 rounded px-1.5 py-1 bg-white"}
      value={value}
      onChange={(e) => e.target.value && onChange(e.target.value)}
    >
      {opts.map((ym) => (
        <option key={ym} value={ym}>{ymShortLabel(ym)}</option>
      ))}
    </select>
  );
}

// [2026-07-18] 旧 DpcBranchChips (チップ表示) は DpcBranchSelector (独立セレクタ) に置換した。

// [2026-07-18 ユーザ指示] DPC 絞り込み (疾患+手術分類+処置1/2+13/14桁選択) の一括リセット。
// 何か設定されている時だけ表示。setDpc6(null) の副作用で下位が全連鎖クリアされる。
function DpcClearAllButton() {
  const f = useFilters();
  const anySet = f.dpc6 || f.selectedDpc7 || f.selectedDpc8 || f.selectedDpc10 || f.selectedDpc11 || f.selectedDpc12 || f.selectedDpc13 || f.selectedDpc14;
  if (!anySet) return null;
  return (
    <button
      type="button"
      // [2026-09-28 ユーザ指示] すべてリセットしたら 疾患別 (6桁) のタブへ戻す
      onClick={() => { f.setDpc6(null); f.setDpcLevel(6); }}
      className="block w-full text-center text-[11px] px-2 py-1 rounded border border-red-200 text-red-600 hover:bg-red-50"
    >
      × DPC絞り込みをすべてリセット
    </button>
  );
}

function Section({
  label, children, right, help,
}: {
  label: string;
  children: React.ReactNode;
  right?: React.ReactNode;
  help?: string;   // [2026-07-17] 補足説明は本文でなく tooltip (ⓘ ホバー) に寄せる
}) {
  return (
    <div>
      <div className="flex items-baseline justify-between mb-1 gap-2">
        <div className="text-xs text-slate-500 font-medium" title={help}>
          {label}
          {help && <span className="ml-1 text-slate-300 cursor-help select-none">ⓘ</span>}
        </div>
        {right && <div className="text-[10px] text-slate-400">{right}</div>}
      </div>
      {children}
    </div>
  );
}

// 「📅 期間」 ラベル右側に表示する default 表記。
// override 中は何も出さず (button が下に出る)、 未 override 時は「直近12ヶ月 (default)」 と表示。
export function DiseasePeriodRightLabel() {
  const f = useFilters();
  const overridden = f.dateFromOverride != null || f.dateToOverride != null;
  if (overridden) return null;
  return <span>直近12ヶ月 (default)</span>;
}

export function RadioGroup({
  name, value, onChange, options,
}: {
  name: string;
  value: string;
  onChange: (v: string) => void;
  options: { value: string; label: string; disabled?: boolean }[];
}) {
  return (
    <div className="flex gap-1 rounded-md bg-slate-100 p-0.5">
      {options.map((opt) => {
        const active = opt.value === value;
        const disabled = !!opt.disabled;
        return (
          <label
            key={opt.value}
            className={[
              "flex-1 text-center px-2 py-1 rounded transition-colors",
              disabled
                ? "text-slate-300 cursor-not-allowed"
                : active
                  ? "bg-white text-brand shadow-sm font-medium cursor-pointer"
                  : "text-slate-600 hover:bg-white/60 cursor-pointer",
            ].join(" ")}
          >
            <input
              type="radio"
              name={name}
              value={opt.value}
              checked={active}
              disabled={disabled}
              onChange={() => !disabled && onChange(opt.value)}
              className="sr-only"
            />
            {opt.label}
          </label>
        );
      })}
    </div>
  );
}
